import { z } from 'zod';
import { slideTtsSettingsSchema } from '../tts/model.js';

/**
 * Narration, per slide.
 *
 * docs/plans/11-m7-recorded-narration.md is explicit that recorded audio (M7)
 * and text-to-speech (M6) "must produce the same NarrationTrack shape so the
 * player and publisher treat them identically". So the shape is defined once,
 * here, and `source` is the only thing that says where the audio came from.
 * When TTS lands it adds a variant, not a parallel data model.
 *
 * The audio itself is NOT in here. A minute of Opus is roughly 180 KB and the
 * settings bag inside a .pptx holds about 1 MB in total (SPIKE 2), so a single
 * narrated deck would blow the entire course budget. What is stored is a
 * content hash; the bytes live in the audio store and are written into the
 * package at publish time.
 */

export const NARRATION_SOURCES = ['recorded', 'imported', 'tts'] as const;
export type NarrationSource = (typeof NARRATION_SOURCES)[number];

export const captionCueSchema = z.object({
  startMs: z.number().min(0),
  endMs: z.number().min(0),
  text: z.string(),
});

export const narrationTakeSchema = z.object({
  id: z.string().min(1),
  source: z.enum(NARRATION_SOURCES),
  /** As reported by MediaRecorder, e.g. `audio/webm;codecs=opus`. */
  mimeType: z.string().min(1),
  durationMs: z.number().min(0),
  byteLength: z.number().min(0),
  /**
   * SHA-256 of the audio bytes, and the key into the audio store. Content
   * addressing means re-recording identical audio costs nothing and a take can
   * be moved between slides without copying bytes.
   */
  audioId: z.string().min(1),
  recordedAt: z.string().default(''),
  /** Author-visible name. Takes are otherwise indistinguishable. */
  label: z.string().default(''),
  /** Present for generated audio; identical inputs resolve to the same cache entry. */
  synthesisKey: z.string().optional(),
});

export const narrationTrackSchema = z.object({
  takes: z.array(narrationTakeSchema).default([]),
  /**
   * Which take the player uses. Undefined means the slide has takes but none is
   * chosen, which is a state the author can be in deliberately while comparing.
   */
  activeTakeId: z.string().optional(),
  captions: z.array(captionCueSchema).default([]),
  /** Absolute ms offsets for animation sync; written by the manual sync editor. */
  syncMarkers: z.array(z.number().min(0)).default([]),
  /** M6 authoring state. Recorded-only tracks leave this absent. */
  tts: slideTtsSettingsSchema.optional(),
});

export type CaptionCue = z.infer<typeof captionCueSchema>;
export type NarrationTake = z.infer<typeof narrationTakeSchema>;
export type NarrationTrack = z.infer<typeof narrationTrackSchema>;

export function emptyNarrationTrack(): NarrationTrack {
  return narrationTrackSchema.parse({});
}

/** The take the player would use, or undefined when the slide is silent. */
export function activeTakeOf(track: NarrationTrack | undefined): NarrationTake | undefined {
  if (!track || track.takes.length === 0) return undefined;
  if (!track.activeTakeId) return undefined;
  return track.takes.find((take) => take.id === track.activeTakeId);
}

/**
 * Total narration across the deck, and which slides have none.
 *
 * The plan calls this out as the aggregate view that makes narration usable at
 * course scale: a slide the author forgot is invisible one slide at a time.
 */
export function narrationSummary(
  tracks: ReadonlyMap<string, NarrationTrack | undefined>,
  slideIds: readonly string[],
): {
  readonly totalMs: number;
  readonly narratedSlideIds: readonly string[];
  readonly silentSlideIds: readonly string[];
} {
  let totalMs = 0;
  const narrated: string[] = [];
  const silent: string[] = [];

  for (const slideId of slideIds) {
    const take = activeTakeOf(tracks.get(slideId));
    if (take) {
      totalMs += take.durationMs;
      narrated.push(slideId);
    } else {
      silent.push(slideId);
    }
  }

  return { totalMs, narratedSlideIds: narrated, silentSlideIds: silent };
}

/** `7:03`, for the aggregate view and per-take labels. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
